import axios from 'axios';
import TokenService from './token.service';

const baseURL = process.env.REACT_APP_API_URL;

/**
 * Single-flight refresh shared by every axios instance.
 *
 * The backend runs SIMPLE_JWT with ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION,
 * so a refresh token can be spent exactly once. Before this, each 401 started its
 * own POST /auth/refresh/: on first load /auth/account_info/ and /core/exam_years/
 * fire together, both 401 on an expired access token, and the second refresh sent a
 * token the first one had already blacklisted. Whichever request lost that race got
 * its tokens wiped and never delivered its data.
 *
 * Every concurrent caller now awaits the same request.
 */
let refreshPromise = null;

export const refreshAccessToken = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = TokenService.getLocalRefreshToken();

  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token available'));
  }

  refreshPromise = axios
    .post(baseURL + '/auth/refresh/', { refresh: refreshToken })
    .then((res) => {
      const access = res?.data?.access;
      const refresh = res?.data?.refresh;

      if (!access) {
        throw new Error('Refresh response contained no access token');
      }

      TokenService.updateLocalAccessToken(access);

      // Rotation means the token we just spent is now blacklisted. Keeping it
      // would break every later refresh, so store the replacement.
      if (refresh) {
        TokenService.updateRefreshAccessToken(refresh);
      }

      return access;
    })
    .catch((err) => {
      TokenService.removeTokens();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

export default refreshAccessToken;
